import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { Button, Chip, Field, Modal } from '../components/ui';
import { useAddMembership, useSections } from '../api/queries';
import { useToast } from '../lib/toast';
import type { MemberDetail } from '../api/types';

export function AddMembershipModal({
  open, onClose, member, allowedSectionIds,
}: {
  open: boolean;
  onClose: () => void;
  member: MemberDetail;
  /** null = admin/read_only (alle Sektionen), sonst nur diese */
  allowedSectionIds: string[] | null;
}) {
  const { data: sections = [] } = useSections();
  const add = useAddMembership(member.id);
  const toast = useToast();
  const [section, setSection] = useState<string | null>(null);
  const [status, setStatus] = useState<'mitglied' | 'aktiv' | 'funktionaer' | 'ruhend'>('mitglied');
  const [funktion, setFunktion] = useState<string>('Mitglied');

  useEffect(() => { if (open) { setSection(null); setStatus('mitglied'); setFunktion('Mitglied'); } }, [open]);

  const existing = new Set(member.sections);
  const available = sections.filter((s) => {
    if (existing.has(s.id)) return false;
    if (allowedSectionIds && !allowedSectionIds.includes(s.id)) return false;
    return true;
  });

  const submit = () => {
    if (!section) return;
    add.mutate({ section, status, funktion }, {
      onSuccess: () => { toast.push('Sektion hinzugefügt'); onClose(); },
      onError: () => toast.push('Konnte nicht hinzufügen', 'warn'),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sektion hinzufügen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" icon={<Check size={16} />} onClick={submit} disabled={!section || add.isPending}>
            Hinzufügen
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {available.length === 0 ? (
          <div className="muted-card" style={{ fontSize: 13.5 }}>
            {existing.size === sections.length
              ? 'Mitglied ist bereits in allen Sektionen.'
              : 'Keine weiteren Sektionen verfügbar (rollenbedingt).'}
          </div>
        ) : (
          <>
            <Field label="Sektion">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {available.map((s) => (
                  <Chip key={s.id} active={section === s.id} onClick={() => setSection(s.id)}>
                    <span className="sec-dot" style={{ background: s.color }} />
                    {s.name}
                  </Chip>
                ))}
              </div>
            </Field>
            <Field label="Status">
              <div className="segmented">
                {(['mitglied', 'aktiv', 'funktionaer', 'ruhend'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={status === s ? 'active' : ''}
                    onClick={() => setStatus(s)}
                  >
                    {s === 'funktionaer' ? 'Funktionär' : s}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Funktion in dieser Sektion">
              <select className="input" value={funktion} onChange={(e) => setFunktion(e.target.value)}>
                {['Mitglied', 'Obmann', 'Obmann Stv.', 'Sektionsleiter', 'Sektionsleiter Stv.', 'Kassier', 'Kassier Stv.', 'Schriftführer', 'Schriftführer Stv.', 'Beirat', 'Trainer', 'Spieler'].map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}
